import { useState } from 'react';
import { useI18n } from '@react/i18n';
import { Page, ReturnedItem, TypVraceni, UserInput, VratkaItem } from '../types';
import { UpdateUserInput } from '../VratkyApp';

interface Props {
    userInput: UserInput;
    update: UpdateUserInput;
    products: VratkaItem[];
    typVraceni: TypVraceni;
    link: (page: Page) => void;
}

function isInt(value: number | string): boolean {
    return String(value) !== '' && Number.isInteger(Number(value));
}

export function ProductSelector({ userInput, update, products, typVraceni, link }: Props) {
    const t = useI18n();
    const [error, setError] = useState<'no_returned_items' | 'missing_amounts' | null>(null);

    const isSelected = (code: string) => userInput.vracene_polozky.some((e) => e.product_code === code);

    const toggleProduct = (item: VratkaItem) => {
        if (isSelected(item.product_code)) {
            update('vracene_polozky', userInput.vracene_polozky.filter((e) => e.product_code !== item.product_code));
            return;
        }
        // u vice objednanych ks musi zakaznik pocet zadat explicitne, jinak delaji chyby
        const returned: ReturnedItem = { ...item, amount: item.amount > 1 ? '' : 1 };
        update('vracene_polozky', [...userInput.vracene_polozky, returned]);
    };

    const changeAmount = (value: string, item: VratkaItem) => {
        const amount = Number(value) > item.amount ? item.amount : value;
        update('vracene_polozky', userInput.vracene_polozky.map(
            (el) => el.product_code === item.product_code ? { ...el, amount } : el,
        ));
    };

    const forward = () => {
        if (userInput.vracene_polozky.length === 0) {
            setError('no_returned_items');
        } else if (userInput.vracene_polozky.some((el) => !el.amount || !isInt(el.amount))) {
            setError('missing_amounts');
        } else {
            link('form');
        }
    };

    return (
        <div className="vr-step">
            <h3>{typVraceni === 'vratka' ? t('Prosíme vyberte vracené produkty') : t('Prosíme vyberte reklamované produkty')}</h3>

            {products.map((item) => {
                const selected = isSelected(item.product_code);
                const returned = userInput.vracene_polozky.find((e) => e.product_code === item.product_code);
                return (
                    <div key={item.product_code} className="vr-card vr-card--product">
                        <button
                            type="button"
                            className={selected ? 'vr-btn vr-btn--secondary vr-card__action' : 'vr-btn vr-btn--primary vr-card__action'}
                            onClick={() => toggleProduct(item)}
                        >
                            {selected
                                ? t('Zrušit')
                                : typVraceni === 'vratka' ? t('Označit k vrácení') : t('Označit k reklamaci')}
                        </button>
                        <h4 className="vr-card__title">{item.product_name}</h4>
                        <div className="vr-card__subtitle">{item.product_code}</div>
                        <div className="vr-card__subtitle">{t('Koupeno ks:')} {item.amount}</div>
                        {selected && returned && (
                            <>
                                <div className="vr-alert vr-alert--info">
                                    {typVraceni === 'vratka' ? t('Zahrnuto do vratky') : t('Zahrnuto do reklamace')}
                                </div>
                                <label>{t('Počet ks:')}</label>
                                <input
                                    className="form-control vr-amount"
                                    type="number"
                                    min={1}
                                    max={item.amount}
                                    value={returned.amount}
                                    onChange={(e) => changeAmount(e.target.value, item)}
                                />
                            </>
                        )}
                    </div>
                );
            })}

            {error === 'no_returned_items' && userInput.vracene_polozky.length === 0 && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Vyberte alespoň jeden produkt')}</div>
            )}
            {error === 'missing_amounts' && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Zadejte množství produktu')}</div>
            )}

            <div className="vr-actions">
                <button type="button" className="vr-btn vr-btn--secondary" onClick={() => link('baseInput')}>{t('Zpět')}</button>
                <button type="button" className="vr-btn vr-btn--primary" onClick={forward}>{t('Pokračovat')}</button>
            </div>
        </div>
    );
}
